import { Controller } from '@hotwired/stimulus'
import ImageProcessor from '../lib/image_processor'

const MAX_BYTES = 110 * 1024 * 1024 // 110MB
const BAR_MAX_DPI = 300

const gcd = (a, b) => b ? gcd(b, a % b) : a
const fmt = n => n.toLocaleString('en-US')
const fmtBytes = b => b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`

// Nearest simple ratio (denominator up to 16), e.g. 6000 × 4000 → "3:2"
const approxRatio = (w, h) => {
  let best = [1, 1]
  let err = Infinity
  for (let d = 1; d <= 16; d++) {
    const n = Math.round(w / h * d)
    const e = Math.abs(n / d - w / h)
    if (n > 0 && e < err - 1e-9) {
      err = e
      best = [n, d]
    }
  }
  const g = gcd(best[0], best[1])
  return `${best[0] / g}:${best[1] / g}`
}

const MAX_PIXELS = 100000
const num = str => Number(str.replace(/,/g, ''))

// "6000 x 4000", "6,000×4,000 px", "6000 by 4000" → { w, h }
// "2:3", "1.5:1", "4/5" → { ratio: [2, 3] } (width:height)
const parseManualSize = (raw) => {
  const text = raw.trim().toLowerCase()
  const pixels = text.match(/^(\d[\d,]*)\s*(?:x|×|\*|by|\s)\s*(\d[\d,]*)\s*(?:px)?$/)
  if (pixels) {
    const w = num(pixels[1])
    const h = num(pixels[2])
    if (!Number.isInteger(w) || !Number.isInteger(h) || w < 1 || h < 1) return null
    if (w > MAX_PIXELS || h > MAX_PIXELS) return null
    return { w, h }
  }

  const ratio = text.match(/^(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)$/)
  if (ratio) {
    const a = Number(ratio[1])
    const b = Number(ratio[2])
    if (!(a > 0 && b > 0) || Math.max(a / b, b / a) > 10) return null
    return { ratio: [a, b] }
  }

  return null
}

// A blank frame in the given shape to stand in for the photo
const placeholderSvg = (w, h) => {
  const scale = 1200 / Math.max(w, h)
  const sw = Math.round(w * scale)
  const sh = Math.round(h * scale)
  const icon = Math.min(sw, sh) * 0.18
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${sw}" height="${sh}" viewBox="0 0 ${sw} ${sh}">`
    + `<rect width="100%" height="100%" fill="#e4e5e7"/>`
    + `<g transform="translate(${(sw - icon) / 2} ${(sh - icon) / 2}) scale(${icon / 24})" fill="none" stroke="#9a9ea3" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">`
    + `<rect x="3" y="3" width="18" height="18"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/></g>`
    + `</svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

const UNITS_KEY = 'print-check:units'
const INCH_REGIONS = ['US', 'LR', 'MM']
const cm = inches => Math.round(inches * 2.54)

// A saved choice wins; otherwise inches only for the regions that use them
const initialUnits = () => {
  try {
    const saved = localStorage.getItem(UNITS_KEY)
    if (saved === 'in' || saved === 'cm') return saved
  }
  catch { /* storage blocked */ }

  const region = (navigator.languages || [navigator.language])
    .map((tag) => {
      try { return new Intl.Locale(tag).maximize().region }
      catch { return null }
    })
    .find(Boolean)
  return !region || INCH_REGIONS.includes(region) ? 'in' : 'cm'
}

export default class extends Controller {
  static targets = [
    'fileInput', 'pickButton', 'status', 'empty', 'results', 'dropOverlay',
    'preview', 'crop', 'cropLabel', 'selLabel', 'name', 'fileMeta',
    'verdictKicker', 'verdictLead', 'verdictSize', 'summary', 'ratios', 'spec',
    'ratioName', 'legend', 'stage', 'boxes', 'maxW', 'maxH', 'rows', 'footnote',
    'ratioTemplate', 'boxTemplate', 'rowTemplate', 'unitInput',
    'manualDialog', 'manualInput', 'manualError', 'resolutionHead',
  ]

  static values = {
    families: Array, // [{ key: '4:5', a: 4, b: 5, sizes: [[8, 10], ...] }], short side first
    minDpi: { type: Number, default: 120 },
  }

  image = null
  ratioKey = null
  sel = null
  loadId = 0

  connect() {
    this.units = initialUnits()
    this.unitInputTargets.forEach((input) => { input.checked = input.value === this.units })
  }

  // — units —

  pickUnits = (e) => {
    this.units = e.target.value
    try { localStorage.setItem(UNITS_KEY, this.units) }
    catch { /* storage blocked: the choice lasts for this visit */ }
    if (this.image) this.render()
  }

  get metric() {
    return this.units === 'cm'
  }

  inchLabel = z => `${z.pw} × ${z.ph}″`

  // Sizes are sold in inches; metric shows the nearest whole centimetre
  sizeLabel = z => this.metric ? `${cm(z.pw)} × ${cm(z.ph)} cm` : this.inchLabel(z)

  shortLabel = z => this.metric ? `${cm(z.pw)}×${cm(z.ph)}` : `${z.pw}×${z.ph}`

  length = inches => this.metric ? `${cm(inches)} cm` : `${inches}″`

  // — file intake —

  pick = () => {
    this.fileInputTarget.click()
  }

  fileChosen = (e) => {
    this.handleFile(e.target.files[0])
    e.target.value = ''
  }

  dragOver = (e) => {
    if (!e.dataTransfer?.types.includes('Files')) return

    e.preventDefault()
    this.dropOverlayTarget.hidden = false
  }

  dragLeave = (e) => {
    // relatedTarget is null once the pointer leaves the window
    if (!e.relatedTarget) this.dropOverlayTarget.hidden = true
  }

  drop = (e) => {
    if (!e.dataTransfer?.types.includes('Files')) return

    e.preventDefault()
    this.dropOverlayTarget.hidden = true
    this.handleFile(e.dataTransfer.files[0])
  }

  // — manual entry —

  openManual = () => {
    this.clearManualError()
    this.manualInputTarget.value = this.image?.manual || ''
    this.manualDialogTarget.showModal()
    this.manualInputTarget.select()
  }

  closeManual = () => {
    this.manualDialogTarget.close()
  }

  clearManualError = () => {
    this.manualErrorTarget.hidden = true
    this.manualInputTarget.removeAttribute('aria-invalid')
  }

  submitManual = (e) => {
    const entry = this.manualInputTarget.value.trim()
    const size = parseManualSize(entry)
    if (!size) {
      e.preventDefault()
      this.manualErrorTarget.textContent = 'Enter pixels like 6000 x 4000, or a ratio like 2:3.'
      this.manualErrorTarget.hidden = false
      this.manualInputTarget.setAttribute('aria-invalid', 'true')
      return
    }

    // a file still being measured shouldn't replace what they typed
    this.loadId++
    this.setBusy(false)

    if (size.ratio) {
      const [a, b] = size.ratio
      // ratio-only: no real pixels, so work on a large frame of that shape
      this.image = { manual: entry, ratioOnly: true, name: `${a}:${b} aspect ratio`, w: Math.round(a / b * 10000), h: 10000 }
    }
    else {
      this.image = { manual: entry, name: `${fmt(size.w)} × ${fmt(size.h)} px`, w: size.w, h: size.h }
    }

    this.previewTarget.src = placeholderSvg(this.image.w, this.image.h)
    this.showResults()
  }

  showResults = () => {
    this.ratioKey = null
    this.sel = null
    this.showStatus(null)
    this.emptyTarget.hidden = true
    this.resultsTarget.hidden = false
    this.buildRatios()
    this.render()
  }

  handleFile = async (file) => {
    if (!file) return

    const processor = new ImageProcessor({ maxWidth: 1200, maxHeight: 1200, defaultQuality: 0.8 })
    if (!processor.checkFileSupport(file.type).supported) {
      return this.showStatus('That file type isn’t supported. Choose a JPG, PNG or TIFF.')
    }
    if (file.size >= MAX_BYTES) {
      return this.showStatus('That file is too large. The maximum is 110 MB.')
    }

    const id = ++this.loadId
    this.showStatus(`Measuring ${file.name}…`)
    this.setBusy(true)

    try {
      const result = await processor.processImage(file)
      if (id !== this.loadId) return
      if (!result.width || !result.height) throw new Error('Could not read the image size')

      this.image = {
        name: file.name,
        bytes: file.size,
        type: (file.type.split('/')[1] || '').toUpperCase(),
        w: result.width,
        h: result.height,
      }
      this.previewTarget.src = result.dataUrl
      this.showResults()
    }
    catch (error) {
      if (id !== this.loadId) return
      console.error('Print check failed:', error)
      this.showStatus('We couldn’t read that image. Try exporting it again as a JPG.')
    }
    finally {
      if (id === this.loadId) this.setBusy(false)
    }
  }

  showStatus = (message) => {
    this.statusTarget.textContent = message || ''
    this.statusTarget.hidden = !message
  }

  setBusy = (busy) => {
    this.pickButtonTargets.forEach((b) => { b.disabled = busy })
  }

  // — interaction —

  pickRatio = (e) => {
    this.ratioKey = e.target.value
    this.sel = null
    this.render()
  }

  pickSize = (e) => {
    this.sel = Number(e.currentTarget.dataset.index)
    this.renderSelection()
  }

  // — calculations —

  get portrait() {
    return this.image.h >= this.image.w
  }

  // width ÷ height of a family in the image's orientation
  targetRatio = (family) => {
    return this.portrait ? family.a / family.b : family.b / family.a
  }

  ratioLabel = (family) => {
    return this.portrait ? `${family.a}:${family.b}` : `${family.b}:${family.a}`
  }

  get bestKey() {
    const r = this.image.w / this.image.h
    const distance = f => Math.abs(Math.log(r / this.targetRatio(f)))
    return this.familiesValue.reduce((best, f) => distance(f) < distance(best) ? f : best).key
  }

  compute = () => {
    const { w, h } = this.image
    const minDpi = this.minDpiValue
    const family = this.familiesValue.find(f => f.key === (this.ratioKey || this.bestKey))
    const r = w / h
    const t = this.targetRatio(family)
    const cw = r > t ? Math.round(h * t) : w
    const ch = r > t ? h : Math.round(w / t)

    const sizes = family.sizes.map(([s, l]) => {
      const pw = this.portrait ? s : l
      const ph = this.portrait ? l : s
      if (this.image.ratioOnly) return { pw, ph, dpi: null, tier: null, ok: true, needW: Math.ceil(pw * minDpi), needH: Math.ceil(ph * minDpi) }

      const dpi = Math.floor(Math.min(cw / pw, ch / ph))
      const tier = dpi >= 300
        ? 'Gallery'
        : dpi >= Math.max(minDpi, 200) ? 'Excellent' : dpi >= minDpi ? 'Good' : dpi >= minDpi * 0.7 ? 'Soft' : 'Too low'
      return { pw, ph, dpi, tier, ok: dpi >= minDpi }
    })
    const lastOk = sizes.findLastIndex(z => z.ok)

    return { family, cw, ch, sizes, lastOk, minDpi, ratio: this.ratioLabel(family) }
  }

  // — rendering —

  buildRatios = () => {
    const bestKey = this.bestKey
    this.ratiosTarget.replaceChildren(...this.familiesValue.map((f) => {
      const option = this.ratioTemplateTarget.content.firstElementChild.cloneNode(true)
      const input = option.querySelector('input')
      input.value = f.key
      input.checked = f.key === bestKey
      option.querySelector('[data-label]').textContent = this.ratioLabel(f)
      option.querySelector('[data-best]').hidden = f.key !== bestKey
      return option
    }))
  }

  render = () => {
    if (this.image.ratioOnly) return this.renderRatioOnly()

    const s = this.image
    const c = this.compute()
    const best = c.lastOk >= 0 ? c.sizes[c.lastOk] : null
    const kept = Math.round(c.cw * c.ch / (s.w * s.h) * 100)
    this.c = c

    this.nameTarget.textContent = s.name
    this.fileMetaTarget.textContent = s.manual
      ? 'Entered by hand · no file'
      : [s.type, s.bytes ? fmtBytes(s.bytes) : null, `${fmt(s.w)} × ${fmt(s.h)} px`].filter(Boolean).join(' · ')

    this.setCrop(c)
    this.cropLabelTarget.textContent = `${c.ratio} crop · ${fmt(c.cw)} × ${fmt(c.ch)}`

    this.verdictKickerTarget.textContent = best
      ? ['Recommended', this.metric && `${this.inchLabel(best)} print`, `${best.dpi} dpi`].filter(Boolean).join(' · ')
      : 'Below print quality'
    this.verdictLeadTarget.textContent = best ? 'Prints sharp up to' : 'Too small for'
    this.verdictSizeTarget.textContent = this.sizeLabel(best || c.sizes[0])
    this.summaryTarget.textContent = best
      ? `At ${fmt(s.w)} × ${fmt(s.h)} px your image is roughly ${approxRatio(s.w, s.h)}. Cropped to ${c.ratio} it keeps ${kept}% of the frame and holds at least ${c.minDpi} dpi up to ${this.sizeLabel(best)}. Larger sizes will print, but softer.`
      : `At ${fmt(s.w)} × ${fmt(s.h)} px there aren’t enough pixels for ${c.minDpi} dpi at any ${c.ratio} size. Try a higher-resolution export.`

    const specs = {
      pixels: `${fmt(s.w)} × ${fmt(s.h)}`,
      megapixels: (s.w * s.h / 1e6).toFixed(1),
      native_ratio: approxRatio(s.w, s.h),
      print_crop: `${fmt(c.cw)} × ${fmt(c.ch)}`,
      frame_kept: `${kept}%`,
      orientation: s.w === s.h ? 'Square' : this.portrait ? 'Portrait' : 'Landscape',
    }
    this.specTargets.forEach((dd) => { dd.textContent = specs[dd.dataset.key] })

    this.ratioNameTarget.textContent = c.ratio
    this.legendTarget.textContent = `Solid = ${c.minDpi}+ dpi · tap a size to preview`
    this.footnoteTarget.textContent = `DPI is measured on the ${c.ratio} crop. The tick on each bar marks ${c.minDpi} dpi, our minimum for a sharp print at normal viewing distance.${this.metric ? ' Prints are made in inch sizes; centimetres are rounded.' : ''}`

    this.renderSizes()
    this.renderSelection()
  }

  // Only a shape was entered: list the sizes in it and the pixels each one needs
  renderRatioOnly = () => {
    const s = this.image
    const c = this.compute()
    const largest = c.sizes[c.sizes.length - 1]
    const native = approxRatio(s.w, s.h)
    const kept = Math.round(c.cw * c.ch / (s.w * s.h) * 100)
    this.c = c

    this.nameTarget.textContent = s.name
    this.fileMetaTarget.textContent = 'Entered by hand · no pixel size'
    this.setCrop(c)
    this.cropLabelTarget.textContent = `${c.ratio} crop`

    this.verdictKickerTarget.textContent = `${c.sizes.length} sizes at ${c.ratio}`
    this.verdictLeadTarget.textContent = 'Prints up to'
    this.verdictSizeTarget.textContent = this.sizeLabel(largest)
    this.summaryTarget.textContent = native === c.ratio
      ? `A ${native} image prints at every ${c.ratio} size with no cropping. For ${this.sizeLabel(largest)} at ${c.minDpi} dpi you need at least ${fmt(largest.needW)} × ${fmt(largest.needH)} px. Add your image to check its exact sizes.`
      : `A ${native} image cropped to ${c.ratio} keeps ${kept}% of the frame. For ${this.sizeLabel(largest)} at ${c.minDpi} dpi the crop needs at least ${fmt(largest.needW)} × ${fmt(largest.needH)} px.`

    const specs = {
      pixels: '—',
      megapixels: '—',
      native_ratio: native,
      print_crop: c.ratio,
      frame_kept: `${kept}%`,
      orientation: s.w === s.h ? 'Square' : this.portrait ? 'Portrait' : 'Landscape',
    }
    this.specTargets.forEach((dd) => { dd.textContent = specs[dd.dataset.key] })

    this.ratioNameTarget.textContent = c.ratio
    this.legendTarget.textContent = 'Tap a size to preview'
    this.footnoteTarget.textContent = `Pixels needed are for ${c.minDpi} dpi, our minimum for a sharp print at normal viewing distance. For gallery quality, aim for 300 dpi.${this.metric ? ' Prints are made in inch sizes; centimetres are rounded.' : ''}`

    this.renderSizes()
    this.renderSelection()
  }

  setCrop = (c) => {
    const s = this.image
    Object.assign(this.cropTarget.style, {
      left: `${(s.w - c.cw) / 2 / s.w * 100}%`,
      top: `${(s.h - c.ch) / 2 / s.h * 100}%`,
      width: `${c.cw / s.w * 100}%`,
      height: `${c.ch / s.h * 100}%`,
    })
  }

  renderSizes = () => {
    const c = this.c
    const L = c.sizes[c.sizes.length - 1]

    Object.assign(this.stageTarget.style, { maxWidth: this.portrait ? '300px' : '440px', aspectRatio: `${L.pw} / ${L.ph}` })
    this.maxWTarget.textContent = this.length(L.pw)
    this.maxHTarget.textContent = this.length(L.ph)

    this.boxesTarget.replaceChildren(...c.sizes.map((z, i) => {
      const box = this.boxTemplateTarget.content.firstElementChild.cloneNode(true)
      box.dataset.index = i
      box.dataset.ok = z.ok
      box.title = this.sizeLabel(z)
      box.setAttribute('aria-label', z.dpi === null ? this.sizeLabel(z) : `${this.sizeLabel(z)}, ${z.dpi} dpi`)
      Object.assign(box.style, { width: `${z.pw / L.pw * 100}%`, height: `${z.ph / L.ph * 100}%`, zIndex: c.sizes.length - i })
      box.querySelector('[data-tag]').textContent = this.shortLabel(z)
      return box
    }))

    const ratioOnly = this.image.ratioOnly
    this.resolutionHeadTarget.textContent = ratioOnly ? 'Pixels needed' : 'Resolution'

    this.rowsTarget.replaceChildren(...c.sizes.map((z, i) => {
      const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
      row.dataset.index = i
      row.dataset.ok = z.ok
      row.querySelector('[data-label]').textContent = this.sizeLabel(z)
      const alt = row.querySelector('[data-alt]')
      alt.textContent = this.inchLabel(z)
      alt.hidden = !this.metric
      row.querySelector('[data-dpi]').textContent = ratioOnly ? c.minDpi : z.dpi
      const tier = row.querySelector('[data-tier]')
      if (ratioOnly) {
        row.querySelector('[data-meter]').hidden = true
        const need = row.querySelector('[data-need]')
        need.textContent = `${fmt(z.needW)} × ${fmt(z.needH)}`
        need.hidden = false
        tier.hidden = true
        return row
      }

      row.querySelector('[data-bar]').style.width = `${Math.min(z.dpi / BAR_MAX_DPI, 1) * 100}%`
      row.querySelector('[data-mark]').style.left = `${c.minDpi / BAR_MAX_DPI * 100}%`
      tier.textContent = z.tier
      tier.dataset.tone = !z.ok ? 'neutral' : z.tier === 'Good' ? 'outline' : 'accent'
      return row
    }))
  }

  renderSelection = () => {
    const c = this.c
    const selIdx = this.sel ?? Math.max(c.lastOk, 0)
    const sel = c.sizes[selIdx]

    const state = (z, i) => i === selIdx ? 'on' : z.ok ? 'ok' : 'off'
    Array.from(this.boxesTarget.children).forEach((box, i) => {
      box.dataset.state = state(c.sizes[i], i)
      box.setAttribute('aria-pressed', i === selIdx)
      // the two smallest outlines are too cramped for a label unless selected
      box.querySelector('[data-tag]').hidden = i < 2 && i !== selIdx
    })
    Array.from(this.rowsTarget.children).forEach((row, i) => {
      row.dataset.state = state(c.sizes[i], i)
      row.setAttribute('aria-pressed', i === selIdx)
    })

    this.selLabelTarget.textContent = sel.dpi === null ? this.sizeLabel(sel) : `${this.sizeLabel(sel)} · ${sel.dpi} dpi`
  }
}
